/**
 * selectedDayReadout (#2456 Ask 4) — the pure mapping behind the click-to-select
 * "Ausgewählter Tag" readout that replaces the removed hover tooltip. Given the
 * selected chart row + which series are currently VISIBLE, it produces one
 * readout line per visible series with the SAME values the chart draws at that
 * bucket — so the readout always reconciles to the plot. No React, no recharts
 * (recharts does not render under jsdom): the value mapping is unit tested
 * directly, the component only formats + lays it out.
 *
 * #2544-C — each line now carries BOTH semantics, which is what closed the
 * #2544-B gap where the readout reported the running total in Einzelernte while
 * the plot's own selection dot sat at the bucket amount:
 *
 *   ``value``       — the cumulative (``*Cum``), "where the season stands here"
 *   ``bucketValue`` — the per-period (``*Bucket``), "what came in during this one"
 *
 * Both are read STRAIGHT off the row and neither is derived from the other. That
 * is a correctness rule, not a style preference: the cumulative Prognose carries
 * the #2455 seed and the #2490 monotonic clamps, so ``diff(forecastCum)`` would
 * report a phantom first-bucket harvest and post-clamp zeros. The row's
 * ``*Bucket`` fields are the raw per-bucket sums the cumulative was itself built
 * from (``bucketWithinRange``, ``outputSeries.ts``), so plot and readout are ONE
 * source rather than two derivations — asserted in ``selectedDayReadout.test.ts``.
 */
import type { OutputChartRow } from './outputSeries';
import type { PriorYearToggle } from './OutputSeriesChart';

export interface ReadoutLine {
  /** Stable key for the row (also the React key). */
  key: string;
  /** German label — matches the chart legend / prior-year toggle label. */
  label: string;
  /** Cumulative value at the selected bucket, or null (⇒ the component renders an em dash). */
  value: number | null;
  /**
   * Amount attributable to the selected bucket ALONE ("Ernte im Zeitraum") —
   * the row's own ``*Bucket`` field, verbatim. Never recomputed here or in the
   * panel (see the module header). ``0`` is a truthful "nothing in this bucket"
   * and must survive as 0; ``null`` means outside the series' data range (⇒ em
   * dash), as does the Plan line, which has no per-period value at all.
   */
  bucketValue: number | null;
}

export interface SelectedDayReadoutOptions {
  /** The horizon overlay ("Prognose vor N Tagen") is currently drawn. */
  showHorizon: boolean;
  /** N for the horizon label; only read when ``showHorizon``. */
  horizonDays: number;
  /** A plan target line is currently drawn. */
  hasPlan: boolean;
  /** The available prior-year overlays + their toggle state (only enabled ones show). */
  priorYears: PriorYearToggle[];
}

const priorYearValue = (row: OutputChartRow, offset: number): number | null => {
  switch (offset) {
    case 1:
      return row.priorYear1;
    case 2:
      return row.priorYear2;
    case 3:
      return row.priorYear3;
    default:
      return null;
  }
};

/** The per-bucket twin of {@link priorYearValue} — same offset, ``*Bucket`` field. */
const priorYearBucketValue = (row: OutputChartRow, offset: number): number | null => {
  switch (offset) {
    case 1:
      return row.priorYear1Bucket;
    case 2:
      return row.priorYear2Bucket;
    case 3:
      return row.priorYear3Bucket;
    default:
      return null;
  }
};

/** The chart row for a selected bucket key, or null when it left the data range
 *  (a stale ``?d=`` after a grain/zoom change, or no selection yet). */
export function findRowByBucket(rows: OutputChartRow[], bucket: string | null): OutputChartRow | null {
  if (!bucket) return null;
  return rows.find((r) => r.bucket === bucket) ?? null;
}

/**
 * One readout line per series VISIBLE on the chart, in the chart's reading order:
 * Ist, Prognose, [horizon overlay], [plan], each ENABLED Vorjahr. Ist + Prognose
 * always render (a null value ⇒ an em dash); the overlay/plan/prior-year lines
 * render only when their series is drawn.
 *
 * Every line pairs the row's cumulative with the row's per-bucket amount, both
 * copied field-for-field. Because the panel renders both columns in BOTH data
 * modes, whichever number the plot is currently drawing is always present in the
 * readout — so the two agree in Einzelernte as well as Kumuliert, which they did
 * not between #2544-B and #2544-C. See the module header.
 */
export function buildSelectedDayReadout(row: OutputChartRow, opts: SelectedDayReadoutOptions): ReadoutLine[] {
  const lines: ReadoutLine[] = [
    { key: 'ist', label: 'Ist', value: row.actualCum, bucketValue: row.actualBucket },
    { key: 'prognose', label: 'Prognose', value: row.forecastCum, bucketValue: row.forecastBucket },
  ];
  if (opts.showHorizon) {
    lines.push({
      key: 'horizon',
      label: `Prognose vor ${opts.horizonDays} Tagen`,
      value: row.horizonCum,
      bucketValue: row.horizonBucket,
    });
  }
  if (opts.hasPlan) {
    // No ``bucketValue``: ``OutputChartRow`` deliberately carries no
    // ``planBucket`` — a yield_plan is a season SCALAR, and slicing it per
    // bucket would invent a plan curve nobody entered.
    lines.push({ key: 'plan', label: 'Plan (Ziel)', value: row.planTarget, bucketValue: null });
  }
  for (const py of opts.priorYears) {
    if (!py.enabled) continue;
    lines.push({
      key: `py-${py.offset}`,
      label: py.label,
      value: priorYearValue(row, py.offset),
      bucketValue: priorYearBucketValue(row, py.offset),
    });
  }
  return lines;
}
